import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { db } from "../../services/api";
import { productService } from "../../services/productService";
import { procurementService } from "../../services/procurementService";
import { warehouseService } from "../../services/warehouseService";
import { salesService } from "../../services/salesService";
import { financeService, auditService } from "../../services/financeService";
import { calculateDaysToExpiry } from "../../utils/calculations";

const AppContext = createContext();

export function AppProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [stockTransfers, setStockTransfers] = useState([]);
  const [grns, setGrns] = useState([]);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [returns, setReturns] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);

  // Load all initial state
  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        prods,
        whs,
        supps,
        custs,
        btchs,
        pos,
        sos,
        trfs,
        grnList,
        pays,
        exps,
        rets,
        emps,
        audits
      ] = await Promise.all([
        productService.getProducts(),
        warehouseService.getWarehouses(),
        procurementService.getSuppliers(),
        salesService.getCustomers(),
        productService.getBatches(),
        procurementService.getPurchaseOrders(),
        salesService.getSalesOrders(),
        warehouseService.getStockTransfers(),
        procurementService.getGRNs(),
        financeService.getPayments(),
        financeService.getExpenses(),
        financeService.getReturns(),
        financeService.getEmployees(),
        auditService.getLogs()
      ]);

      setProducts(prods);
      setWarehouses(whs);
      setSuppliers(supps);
      setCustomers(custs);
      setBatches(btchs);
      setPurchaseOrders(pos);
      setSalesOrders(sos);
      setStockTransfers(trfs);
      setGrns(grnList);
      setPayments(pays);
      setExpenses(exps);
      setReturns(rets);
      setEmployees(emps);
      setAuditLogs(audits);
    } catch (e) {
      console.error("Error loading application state:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Dynamic Aggregate Metrics (Live computations)
  const metrics = useMemo(() => {
    const totalSalesAmount = salesOrders.reduce((sum, so) => sum + (Number(so.grandTotal) || 0), 0);
    const totalPurchasesAmount = purchaseOrders.reduce((sum, po) => sum + (Number(soTotal(po)) || 0), 0);
    const totalProfit = salesOrders.reduce((sum, so) => sum + (Number(so.totalProfit) || 0), 0);
    const grossMargin = totalSalesAmount > 0 ? Number(((totalProfit / totalSalesAmount) * 100).toFixed(2)) : 0;

    const totalStockValue = batches.reduce((sum, b) => {
      if (b.status === "Expired") return sum;
      return sum + (Number(b.qtyRemaining) || 0) * (Number(b.unitCost) || 0);
    }, 0);

    // Compute live stock per product
    const productStockMap = {};
    batches.forEach((b) => {
      if (b.status !== "Expired") {
        productStockMap[b.productId] = (productStockMap[b.productId] || 0) + (Number(b.qtyRemaining) || 0);
      }
    });

    const lowStockItems = products.filter((p) => {
      const currentStock = productStockMap[p.id] || 0;
      return currentStock <= p.reorderLevel;
    });

    const expiringSoonBatches = batches.filter((b) => {
      const days = calculateDaysToExpiry(b.expiryDate);
      return days > 0 && days <= 45;
    });

    const expiredBatches = batches.filter((b) => {
      const days = calculateDaysToExpiry(b.expiryDate);
      return days <= 0;
    });

    // Dead Stock: items with stock > 0 but low movement in last 30 days
    const soldProductIds = new Set();
    salesOrders.forEach((so) => {
      so.items?.forEach((i) => soldProductIds.add(i.productId));
    });

    const deadStockProducts = products.filter((p) => {
      const stock = productStockMap[p.id] || 0;
      return stock > 0 && !soldProductIds.has(p.id);
    });

    const deadStockValue = deadStockProducts.reduce((sum, p) => {
      const stock = productStockMap[p.id] || 0;
      return sum + stock * p.purchasePrice;
    }, 0);

    const outstandingReceivables = customers.reduce((sum, c) => sum + (Number(c.outstandingBalance) || 0), 0);
    const outstandingPayables = suppliers.reduce((sum, s) => sum + (Number(s.outstandingBalance) || 0), 0);
    const totalExpensesAmount = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    const estimatedNetProfit = totalProfit - totalExpensesAmount;

    return {
      totalSalesAmount,
      totalPurchasesAmount,
      totalProfit,
      grossMargin,
      totalStockValue,
      lowStockCount: lowStockItems.length,
      lowStockItems,
      expiringSoonCount: expiringSoonBatches.length,
      expiringSoonBatches,
      expiredCount: expiredBatches.length,
      deadStockValue,
      deadStockProducts,
      outstandingReceivables,
      outstandingPayables,
      totalExpensesAmount,
      estimatedNetProfit,
      productStockMap
    };

    function soTotal(po) {
      return po.landedCost || po.totalAmount || 0;
    }
  }, [salesOrders, purchaseOrders, batches, products, customers, suppliers, expenses]);

  // Actions
  const addProduct = async (productData) => {
    const created = await productService.createProduct(productData);
    await loadAllData();
    return created;
  };

  const updateProduct = async (id, updates) => {
    const updated = await productService.updateProduct(id, updates);
    await loadAllData();
    return updated;
  };

  const deleteProduct = async (id) => {
    await productService.deleteProduct(id);
    await loadAllData();
    return true;
  };

  const addSupplier = async (supplierData) => {
    const created = await procurementService.createSupplier(supplierData);
    await loadAllData();
    return created;
  };

  const updateSupplier = async (id, updates) => {
    const updated = await procurementService.updateSupplier(id, updates);
    await loadAllData();
    return updated;
  };

  const addPurchaseOrder = async (poData) => {
    const created = await procurementService.createPurchaseOrder(poData);
    await loadAllData();
    return created;
  };

  const updatePOStatus = async (poId, newStatus) => {
    const updated = await procurementService.updatePOStatus(poId, newStatus);
    await loadAllData();
    return updated;
  };

  const receiveGRN = async (grnData) => {
    const grn = await procurementService.receiveGRN(grnData);
    await loadAllData();
    return grn;
  };

  const addStockTransfer = async (transferData) => {
    const created = await warehouseService.createStockTransfer(transferData);
    await loadAllData();
    return created;
  };

  const completeStockTransfer = async (transferId) => {
    const completed = await warehouseService.completeStockTransfer(transferId);
    await loadAllData();
    return completed;
  };

  const processSale = async (saleData) => {
    const order = await salesService.processSale(saleData);
    await loadAllData();
    return order;
  };

  const addCustomer = async (custData) => {
    const created = await salesService.createCustomer(custData);
    await loadAllData();
    return created;
  };

  const updateCustomer = async (id, updates) => {
    const updated = await salesService.updateCustomer(id, updates);
    await loadAllData();
    return updated;
  };

  const deleteCustomer = async (id) => {
    await salesService.deleteCustomer(id);
    await loadAllData();
    return true;
  };

  const deleteSupplier = async (id) => {
    await procurementService.deleteSupplier(id);
    await loadAllData();
    return true;
  };

  const addPayment = async (paymentData) => {
    const created = await financeService.recordPayment(paymentData);
    await loadAllData();
    return created;
  };

  const addExpense = async (expenseData) => {
    const created = await financeService.createExpense(expenseData);
    await loadAllData();
    return created;
  };

  const addReturn = async (returnData) => {
    const created = await financeService.createReturn(returnData);
    await loadAllData();
    return created;
  };

  const addEmployee = async (empData) => {
    const created = await financeService.createEmployee(empData);
    await loadAllData();
    return created;
  };

  const adjustStock = async (productId, warehouseId, binCode, physicalCount, systemCount, reason, approver) => {
    const result = await productService.adjustStock(productId, warehouseId, binCode, physicalCount, systemCount, reason, approver);
    await loadAllData();
    return result;
  };

  const resetAllData = () => {
    db.resetDefaults();
    loadAllData();
  };

  return (
    <AppContext.Provider
      value={{
        loading,
        products,
        warehouses,
        suppliers,
        customers,
        batches,
        purchaseOrders,
        salesOrders,
        stockTransfers,
        grns,
        payments,
        expenses,
        returns,
        employees,
        auditLogs,
        metrics,
        refreshData: loadAllData,
        addProduct,
        updateProduct,
        deleteProduct,
        addSupplier,
        updateSupplier,
        deleteSupplier,
        addPurchaseOrder,
        updatePOStatus,
        receiveGRN,
        addStockTransfer,
        completeStockTransfer,
        processSale,
        addCustomer,
        updateCustomer,
        deleteCustomer,
        addPayment,
        addExpense,
        addReturn,
        addEmployee,
        adjustStock,
        resetAllData
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  return useContext(AppContext);
}
